import React from 'react';
import { styles } from './ImageBox.css';
import { ContentfulImage } from '../ContentfulImage/ContentfulImage';
import { Carousel } from '@jbhi-fi/lanyard-ui/components/Carousel';
import { PossiblyResponsiveValue, useResponsiveValue } from '../hooks/useResponsiveValue';
import { useBreakpoint } from '@jbhi-fi/lanyard-ui/utils/useBreakpoint';
import { Link } from '../PlatformFunctionality';

export type ImageBoxProps = {
  src: string;
  alt: string;
  href?: string;
  size?: PossiblyResponsiveValue<number>;
  containerRef?: React.RefObject<HTMLElement>;
};

export const ImageBox = ({ src, alt, href, size: sizeProp = { xs: 100, md: 180 }, containerRef }: ImageBoxProps) => {
  const size = useResponsiveValue(sizeProp, containerRef);

  const contentfulImage = (
    <ContentfulImage
      src={src}
      alt={alt}
      className={styles.image}
      style={{ width: size, height: size }}
      sizes={`${size}px`}
    />
  );

  return href
    ? React.createElement(Link, { className: styles.container, href: href }, contentfulImage)
    : React.createElement(React.Fragment, null, contentfulImage);
};

export type ImageBoxCarouselProps = { imageBoxes: Array<ImageBoxProps>; enableContainerBreakpoints?: boolean };

export const ImageBoxCarousel = ({ imageBoxes, enableContainerBreakpoints = false }: ImageBoxCarouselProps) => {
  const ref = React.useRef<HTMLDivElement>(null);
  const containerRef = enableContainerBreakpoints ? ref : undefined;
  const isContainerMobileSized = useBreakpoint('md', 'down', containerRef);
  const isViewportFullBleed = useBreakpoint('md', 'down');

  return (
    <div ref={containerRef}>
      <Carousel
        showPagination={false}
        slidesPerView="auto"
        slidesOffsetBefore={10}
        slidesPerGroup={isContainerMobileSized ? 2 : 3}
        rewind
        slidesOffsetAfter={isContainerMobileSized ? 0 : 40}
        spaceBetween={10}
        fullBleed={isViewportFullBleed}
        showArrows={true}
        arrowPosition="bottom"
      >
        {imageBoxes.map((imageBoxProps, i) => (
          <ImageBox {...imageBoxProps} key={i} containerRef={containerRef} />
        ))}
      </Carousel>
    </div>
  );
};
